import { FC, Fragment, ReactNode, useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import {
  Box,
  Grid,
  Divider,
  TextField,
  InputLabel,
  MenuItem,
  FormControl,
  Alert,
  Select,
  Collapse,
  IconButton,
  Typography
} from '@mui/material'
import { SelectChangeEvent } from '@mui/material/Select'

import {
  PrepareIssueItem,
  IssueItemRequest,
  TimeTypeEnum,
  EntryType,
  Bill,
  Challan,
} from '../../../services'
import { fetchSlipsListAction } from '../../../store/slips'
// Components
import { AppDialog, ChallanSection, THEME_COLOR } from '../../../components'
import ItemComponent, { HORZ_SPACING } from '../IssueItems/Item'
import PreviewItem from '../IssueItems/PreviewItem'
import { GENERIC_OTHER_CHARGE_ITEM_NAME } from '../IssueItems/otherChargesChallan'

// Iconse
import CloseIcon from '@mui/icons-material/Close'

// Utils
import { ellipsizeString, getFormattedDate } from '../../../utils'

// Redux
import { useAppDispatch, useAppSelector } from '../../../hooks'
import {
  issueItemAction,
  issueItemRequestAction,
  syncSelectOptionsAction,
  fetchtItemOptionsAction,
} from '../../../store/items'
import { showAlertAction } from '../../../store/alerts'
import { fetchCustomerAccountAction } from '../../../store/customerAccounts'
import CustomNumberField from '../Users/profile/CustomNumberField'
import SiteAutocomplete from '../../../../src/components/SitesAutoComplete/SiteAutocomplete'
import { SiteDto } from 'src/components/SitesAutoComplete/Services'
import { useCommandPaletteActions } from '../../../contexts/CommandPaletteContext'

export interface IssueMoreItemProps {
  onClose?: (success?: boolean) => void
  bill_id: number
  customer_id: number
  selectedDate?: string
  billInfo?: Bill
  billType: string
  challanNo?: string
  /** Pre-select dropdown by server challan id (avoids wrong row when slip numbers repeat across FY). */
  initialChallanId?: number
  siteTransfer?: boolean
  /** When true, do not navigate to view-slips on success (caller handles refresh) */
  skipNavigateOnSuccess?: boolean
  mode?: 'ISSUE' | 'EXCESS'
}

const IssueMoreItem: FC<IssueMoreItemProps> = ({
  onClose,
  bill_id,
  customer_id,
  selectedDate,
  billInfo,
  billType,
  challanNo,
  initialChallanId,
  siteTransfer,
  skipNavigateOnSuccess = false,
  mode = 'ISSUE',
}) => {
  const dispatchAction = useAppDispatch()
  const { openNewSite } = useCommandPaletteActions()

  const navigate = useNavigate()
  const { selectOptions, loading } = useAppSelector(
    (state) => state.manageOptionsItems
  )
  const { data: userData } = useAppSelector((state) => state.userData)

  const {
    list: { data: customerAccounts },
  } = useAppSelector((state) => state.manageCustomerAccounts)

  const {
    success: issueItemSuccess,
    error: issueItemFail,
    message: issueErrorMessage,
  } = useAppSelector((state) => state.issueItems)
  const [popupTitle, setPopupTitle] = useState<string | ReactNode>(
    'Issue item '
  )
  const [billId, setBillId] = useState<number>(0)
  const [customerId, setCustomerId] = useState<number>(0)
  const [siteAddress, setSiteAddress] = useState<string | null>(null)
  const [agreementId, setAgreementId] = useState<number | null>(null)
  const [rentType, setRentType] = useState<string | null>(null)
  const [fromSiteId, setFromSiteId] = useState<number>(0)
  const [fromCustomerId, setFromCustomerId] = useState<number>(0)
  const [siteSelectionRefreshKey, setSiteSelectionRefreshKey] = useState<number>(0)


  const [items, setItem] = useState<PrepareIssueItem[]>([])
  const [date, setDate] = useState<Date | null>(null)
  const [deposite, setDeposite] = useState<number | null>(null)
  const [vehicleNo, setVehicleNo] = useState<string | null>(null)
  const [driverName, setDriverName] = useState<string | null>(null)

  const [minRentalDays, setMinRentalDays] = useState<number | null>(null)
  const [timeType, setTimeType] = useState<string>(TimeTypeEnum.MORNING)
  const [isClose, setClosePopup] = useState<boolean>(false)
  const [minRentalDaysError, setMinRentalDaysError] = useState<boolean>(false)
  const [datePickerOpen, setDatePickerOpen] = useState<boolean>(false)
  const [selectedChallan, setSelectedChallan] = useState<Challan>(null)
  const [userDefinedChallanNumber, setDefinedChallanNumber] = useState('')

  const [itemRequest, setItemRequest] = useState<IssueItemRequest | null>(null)
  // All manadatory fields are collected
  const [readyToSubmit, setReadyToSubmit] = useState<boolean>(true)
  const [checked, setChecked] = useState<boolean>(false)

  const isExcessMode = mode === 'EXCESS'

  const onCheckBoxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setChecked(event.target.checked)
  }

  useEffect(() => {
    if (selectedDate) {
      setDate(new Date(selectedDate))
    }
  }, [selectedDate])

  useEffect(() => {
   setFromCustomerId(0)
   setFromSiteId(0)
  }, [])

  useEffect(() => {
    if(bill_id) {
      setBillId(bill_id)
    }
    if(customer_id) {
      setCustomerId(customer_id)
    }
    if(billInfo) {
      setSiteAddress(billInfo.siteAddress)
      setAgreementId(billInfo.agreementId)
    }
    if(billType) {
      setRentType(billType)
    }
    setMinRentalDays(1)
  }, [bill_id, customer_id, billInfo, billType])

  useEffect(() => {
    if (siteTransfer) {
      if(fromSiteId && fromSiteId > 0) {
        dispatchAction(
          fetchtItemOptionsAction(0, 0, fromSiteId)
        )
      }
    } else if (customerId) {
      dispatchAction(
        fetchtItemOptionsAction(customerId, agreementId)
      )
    }
    return () => {
      dispatchAction(syncSelectOptionsAction())
    }
  }, [customerId, agreementId, fromSiteId, siteTransfer, siteSelectionRefreshKey])

  useEffect(() => {
    setItemRequest((request) => {
      const formatedItem =
        items &&
        items.map((data) => {
          const { label, category, ...cleanedItem } = data.item

          const base = {
            ...data, // clone outer PrepareIssueItem
            item: cleanedItem, // item without label/category
            timeType: timeType, // override safely
            ...(data.chargeTitle ? { chargeTitle: data.chargeTitle } : {}),
          }

          return isExcessMode
            ? {
                ...base,
                entryType: EntryType.IN,
                secondaryType: 'EXCESS_QUANTITY',
              }
            : base
        })

      // Remove the old date_entry from request if user is not selected the date
      if (!date && request?.date_entry) {
        delete request.date_entry
      }
      return {
        ...request,
        ...(items && { items: formatedItem as any }),
        ...(customerId && { customerId: customerId }),
        ...(billId && {bill_id: billId }),
        ...(date && { date_entry: getFormattedDate(new Date(date)) }),
        ...(deposite && { deposite }),
      }
    })
  }, [items, timeType, date, deposite, customerId, billId])

  useEffect(() => {
    dispatchAction(
      syncSelectOptionsAction(items, {
        retainOptionLabels: [GENERIC_OTHER_CHARGE_ITEM_NAME],
      })
    )
  }, [items])
  
  useEffect(() => {
    if (isExcessMode) {
      setPopupTitle('Excess Quantity')
      return
    }
    const length = 70
    const givenOrTaken = billInfo?.accountType === "ITEMS_TAKEN" ? "HIRE IN - RECEIVING CHALLAN" : "DELIVERY CHALLAN"
    if(siteTransfer) {
      setPopupTitle('SITE TRANSFER')
    } else if (siteAddress) {
      const title = `${givenOrTaken} (${siteAddress})`
      setPopupTitle(ellipsizeString(title, length))
    } else if (customerAccounts?.length > 0) {
      const billData = customerAccounts.find((account) => account.id == billId)
      const address = billData?.siteAddress ?? ''
      const title = address ? `${givenOrTaken} (${address})` : givenOrTaken
      setPopupTitle(title.includes('(') ? ellipsizeString(title, length)+")" : ellipsizeString(title, length))
    } else {
      setPopupTitle(givenOrTaken)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billId, siteAddress, siteTransfer, isExcessMode])

  useEffect(() => {
    if (issueItemSuccess) {
      setItem([])
      setDeposite(0)
      setItemRequest(null)
      dispatchAction(
        showAlertAction({
          message: 'Item saved Successfully',
          severity: 'success',
        })
      )
      onClose(true)
      dispatchAction(fetchCustomerAccountAction('OPEN'))
      dispatchAction(issueItemRequestAction())
      dispatchAction(fetchSlipsListAction(billId))
      if (!skipNavigateOnSuccess) {
        navigate(`/slips/view-slips/${billId}/${customerId}`)
      }
    } else if (issueErrorMessage) {
      dispatchAction(issueItemRequestAction())
      dispatchAction(
        showAlertAction({
          message: issueErrorMessage,
          severity: 'error',
        })
      )
    }
  }, [issueItemSuccess, issueErrorMessage, dispatchAction])

  const submitHandle = () => {
    if(!itemRequest || !itemRequest.items) {
      return
    }
    const { items, customerId } = itemRequest
    
    if (
      !customerId ||
      (selectedChallan?.id === 0 && !date) ||
      items.length <= 0 ||
      !selectedChallan
    ) {
      if (selectedChallan?.id === 0 && !date) {
        setDatePickerOpen(true)
      }
      setReadyToSubmit(false)
    } else {
      setReadyToSubmit(true)

      if (userData.config.isMinDaysEnabled) {
        if (!minRentalDays || minRentalDays < 1) {
          setMinRentalDaysError(true)
          return
        }
        itemRequest.items.forEach((item, index) => {
          item.minRentalDays = minRentalDays ? minRentalDays : 1
        })
      }
      itemRequest.vehicleNo = vehicleNo
      itemRequest.driverName = driverName
      itemRequest.challanNo = userDefinedChallanNumber
        ? userDefinedChallanNumber
        : selectedChallan?.slipNo
      itemRequest.challanId = selectedChallan?.id as unknown as string
      if(siteTransfer) {
        itemRequest.fromSiteId = fromSiteId
        itemRequest.fromCustomerId = fromCustomerId
      }
      dispatchAction(issueItemAction(itemRequest))
    }
  }

  const addItemHandler = (item) => {
    setReadyToSubmit(true)
    const normalizedItem = isExcessMode ? { ...item, rate: 0 } : item
    const newArray = [normalizedItem, ...items]
    setItem(newArray)
  }

  const deleteItemHandler = (index: number) => {
    setItem(items.filter((_, i) => i !== index))
  }
  // Grouping logic
  const getCategoryCounts = (items: PrepareIssueItem[]) => {
    const counts: Record<string, number> = {}
  
    items.forEach((item) => {
      const category = item.item.category || 'Uncategorized'
      const quantity = Number(item.quantity) || 0
      counts[category] = (counts[category] || 0) + quantity
    })
  
    return counts
  }
  
  const mdValue = 3.5
  const TOP_MARGIN = 0.5
  
  return (
    <AppDialog
      id="issue-more-item"
      title={popupTitle}
      submitButtonText="Submit"
      onClose={onClose}
      onSubmit={submitHandle}
      close={isClose}
      showErrorMessage={issueItemFail}
      headerBgColor={THEME_COLOR}
      headerTextColor={'white'}
      
    >
      <Grid
        container
        direction="row"
        justifyContent="center"
        alignItems="stretch"
        pl={1.5}
        pr={1.5}
        spacing={0}
      >
        {(!bill_id || bill_id === 0) && (
          <>
          { siteTransfer && (
           <Grid item xs={6}>
             <SiteAutocomplete 
               onSiteSelected={(site?: SiteDto) => {
                 setFromSiteId(site?.id)
                 setFromCustomerId(site?.customerId)
                 setSiteSelectionRefreshKey((prev) => prev + 1)
               }} 
               label={siteTransfer ? 'From site' : null}
               onAddNewSite={() => {
                 if (openNewSite) {
                   openNewSite()
                 }
               }}
               onCloseDialog={() => {
                 if (onClose) {
                   onClose()
                 }
               }}
             />
             </Grid>)}
              <Grid item xs={siteTransfer ? 6 : 12} pl={siteTransfer ? 3 : 0}>
              <SiteAutocomplete 
                onSiteSelected={(site?: SiteDto) => {
                  setBillId(site?.id)
                  setSiteAddress(site?.siteAddress)
                  setAgreementId(site?.agreementId)
                  setCustomerId(site?.customerId)
                  setRentType(site?.billType)
                  setSiteSelectionRefreshKey((prev) => prev + 1)
                }} 
                label={siteTransfer ? 'To site' : null}
                blank={siteTransfer}
                onAddNewSite={() => {
                  if (openNewSite) {
                    openNewSite()
                  }
                }}
                onCloseDialog={() => {
                  if (onClose) {
                    onClose()
                  }
                }}
              />
              </Grid>
              </>
            )}
        <Grid item xs={12}>
          <Box
            component="form"
            sx={{
              backgroundColor: 'white',
              '& .MuiTextField-root': { mt: TOP_MARGIN },
            }}
            noValidate
            autoComplete="off"
          >
            <Box>

            {billId != null && billId != undefined && billId > 0 && (
              <ChallanSection
                challanNo={challanNo}
                initialChallanId={initialChallanId}
                userDefinedChallanNumber={userDefinedChallanNumber}
                setDefinedChallanNumber={setDefinedChallanNumber}
                selectedChallan={selectedChallan}
                setSelectedChallan={setSelectedChallan}
                bill_id={billId}
                setReadyToSubmit={setReadyToSubmit}
                datePickerOpen={datePickerOpen}
                setDatePickerOpen={setDatePickerOpen}
                date={date}
                setDate={setDate}
                challan_type={isExcessMode ? 'IN' : 'OUT'}
                mdValue={mdValue}
              />
            )}
            </Box>
          </Box>
          <Collapse in={!readyToSubmit}>
            <Alert
              severity="error"
              action={
                <IconButton
                  aria-label="close"
                  color="inherit"
                  size="small"
                  onClick={() => {
                    setReadyToSubmit(true)
                  }}
                >
                  <CloseIcon fontSize="inherit" />
                </IconButton>
              }
              sx={{ mb: 0, mt: 0 }}
            >
              {items.length == 0
                ? 'It seems you did not click ADD button after filling the item details'
                : 'Please fill all mandatory fields!'}
            </Alert>
          </Collapse>
        </Grid>
        <Grid item xs={12}>
          {/* <Box
            sx={{
              backgroundColor: '#f9f9f9',
              ml: 1,
              mr: 1.8,
              mb: 1,
              borderRadius: 1,
              border: 1,
              borderStyle: 'solid',
              borderColor: '#f0f0f0',
            }}
          >
            <Grid container xs={12}>
              <Grid item xs={6} display={'flex'} alignItems={'center'}>
                <Grid container direction="column">
                  <Grid item xs={12}>
                    <Typography fontWeight={'bold'} ml={1.5}>
                      Select and add items
                    </Typography>
                  </Grid>
                  {billInfo?.ratePerSqft > 0 && (
                    <Grid item xs={12}>
                      <Typography
                        variant="subtitle1"
                        ml={1.5}
                        sx={{ color: 'crimson' }}
                      >
                        For sqft items enter 0 in the below rate field.
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Grid>
              <Grid item xs={5} sx={{ textAlign: 'right', pr: 1, height: 48 }}>
                {billType === 'MIX' && (
                  <>
                    <Checkbox onChange={onCheckBoxChange} /> Set default rate to
                    0
                  </>
                )}
              </Grid>
            </Grid>
          </Box> */}
          <Box
            component="form"
            sx={{
              pt:2,
              '& .MuiTextField-root': { mt: TOP_MARGIN, width: '25ch' },
            }}
            noValidate
            autoComplete="off"
          >
            <ItemComponent
              selectOptions={selectOptions}
              addItem={addItemHandler}
              entryType={isExcessMode ? EntryType.IN : EntryType.OUT}
              submission_type={'SUBMIT'}
              billType={rentType}
              defaultRateZero={checked}
              accountType={billInfo?.accountType}
              billInfo={billInfo}
              onCloseDialog={onClose}
              quantityLabelOverride={isExcessMode ? 'Excess Quantity' : undefined}
              suppressAvailableHelper={isExcessMode}
              ignoreAvailableQuantity={isExcessMode}
              enableRentCyclePlan={!isExcessMode}
              otherChargesAmountUi={true}
            />
            {items.length > 0 && (() => {
              const categoryCounts = getCategoryCounts(items);
              const countText = Object.entries(categoryCounts)
                .map(([category, count]) => `${category}: ${count}`)
                .join(',\u00A0\u00A0\u00A0\u00A0');

              return (
                <div style={{ marginLeft: 0, marginRight: 0 }}>
                  <Typography variant="body2" sx={{ mt: 0, mb: 1 }}>
                    {countText}
                  </Typography>

                  {items.map((item, index) => (
                    <Fragment key={`${item?.item?.id}-${index}`}>
                      <Divider />
                      <PreviewItem
                        item={item}
                        entryType={isExcessMode ? EntryType.IN : EntryType.OUT}
                        deleteItem={() => deleteItemHandler(index)}
                        billType={rentType}
                        isExcessMode={isExcessMode}
                      />
                    </Fragment>
                  ))}
                </div>
              );
            })()}

          </Box>
        </Grid>
      </Grid>
      <Box
        sx={{
          mt: 'auto',
          mr: 0,
          justifyContent: 'flex-end',
        }}
      >
        <Grid
          pl={1.5}
          pr={1.5}
          container
          spacing={0}
          xs={12}
          sx={{ backgroundColor: 'white', paddingBottom: 0, mb: 0 }}
        >
          <Grid item={true} xs={12} md={mdValue}>
            <CustomNumberField
              name="deposite"
              fullWidth
              value={deposite}
              label="Advance Payment"
              onChange={(event: any) => {
                setDeposite(event.target.value)
              }}
              maxLength={8}
            />
          </Grid>
          <Grid
            item={true}
            xs={12}
            md={1.5}
            pl={HORZ_SPACING}
            sx={{ backgroundColor: 'white' }}
          >
            <TextField
              size="small"
              name="vehicleNo"
              fullWidth
              value={vehicleNo}
              label="Vehicle No"
              onChange={(event: any) => {
                if (event.target.value?.length < 50) {
                  setVehicleNo(event.target.value)
                }
              }}
            />
          </Grid>
          <Grid
            item={true}
            xs={12}
            md={2}
            pl={HORZ_SPACING}
            sx={{ backgroundColor: 'white' }}
          >
            <TextField
              size="small"
              name="driverName"
              fullWidth
              value={driverName}
              label="Transporter Name"
              onChange={(event: any) => {
                if (event.target.value?.length <= 100) {
                  setDriverName(event.target.value)
                }
              }}
            />
          </Grid>
          <Grid
            item={true}
            xs={12}
            pl={HORZ_SPACING}
            md={mdValue}
            sx={{ backgroundColor: 'white' }}
          >
            <FormControl sx={{ m: 0 }} fullWidth>
              <InputLabel id="select-time-helper-label">Select Time</InputLabel>
              <Select
                size="small"
                fullWidth
                labelId="select-time-helper-label"
                id="select-time"
                value={timeType}
                name="timeType"
                label="Select Time"
                onChange={(event: SelectChangeEvent) => {
                  setTimeType(event.target.value)
                }}
              >
                <MenuItem value={TimeTypeEnum.MORNING}>Morning</MenuItem>
                <MenuItem value={TimeTypeEnum.EVENING}>
                  Evening (The day is not counted)
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {userData.config.isMinDaysEnabled === true && (
            <Grid
              pl={HORZ_SPACING}
              item={true}
              xs={12}
              md={1.5}
              sx={{ backgroundColor: 'white' }}
            >
              <CustomNumberField
                fullWidth
                name="minRentalDays"
                value={minRentalDays ? minRentalDays : ''}
                error={minRentalDaysError}
                label="Min days"
                onChange={(event: any) => {
                  setMinRentalDays(event.target.value)
                  setMinRentalDaysError(false)
                }}
                maxLength={3}
              ></CustomNumberField>
            </Grid>
          )}
        </Grid>
      </Box>
    </AppDialog>
  )
}

export default IssueMoreItem
